import { InteractionType } from "@azure/msal-browser";
import { ensureAuthenticated } from "@haskoning/msal";
import { defineStore } from "pinia";
import { ref } from "vue";

import { loginRequest, msalInstance } from "@/msalAuthConfig";
import { waitForOffice } from "@/utils/office";

type OfficeState = "idle" | "initializing" | "ready" | "not-in-excel";

type AuthState = "unknown" | "authenticating" | "authenticated" | "unauthenticated";

type BootstrapState = "idle" | "loading" | "ready" | "error";

type RuntimeErrorSource = "office" | "auth" | "request" | "unknown";

type RuntimeError = {
  message: string;
  source: RuntimeErrorSource;
  retriable: boolean;
};

export const useRuntimeStore = defineStore("runtime", () => {
  const officeState = ref<OfficeState>("idle");
  const authState = ref<AuthState>("unknown");
  const bootstrapState = ref<BootstrapState>("idle");
  const runtimeError = ref<RuntimeError | null>(null);

  async function initializeRuntime(): Promise<void> {
    if (bootstrapState.value === "loading") {
      return;
    }

    bootstrapState.value = "loading";
    runtimeError.value = null;
    officeState.value = "initializing";
    authState.value = "unknown";
    let currentPhase: RuntimeError["source"] = "office";

    try {
      const officeAvailable = await waitForOffice();

      if (!officeAvailable) {
        officeState.value = "not-in-excel";
        authState.value = "unauthenticated";
        bootstrapState.value = "ready";
        return;
      }

      const info = await Office.onReady();
      if (info.host !== Office.HostType.Excel) {
        officeState.value = "not-in-excel";
        authState.value = "unauthenticated";
        bootstrapState.value = "ready";
        return;
      }

      officeState.value = "ready";
      currentPhase = "auth";
      authState.value = "authenticating";

      const isAuthenticated = await ensureAuthenticated(msalInstance, InteractionType.Popup, loginRequest);
      if (!isAuthenticated) {
        authState.value = "unauthenticated";
        bootstrapState.value = "error";
        runtimeError.value = {
          message: "Authentication is required to continue.",
          source: "auth",
          retriable: true,
        };
        return;
      }

      authState.value = "authenticated";
      bootstrapState.value = "ready";
    } catch (error: unknown) {
      if (currentPhase === "office") {
        officeState.value = "not-in-excel";
      }
      authState.value = "unauthenticated";
      bootstrapState.value = "error";
      runtimeError.value = {
        message: error instanceof Error ? error.message : "Runtime initialization failed.",
        source: currentPhase,
        retriable: true,
      };
    }
  }

  async function retry(): Promise<void> {
    await initializeRuntime();
  }

  return {
    officeState,
    authState,
    bootstrapState,
    runtimeError,
    initializeRuntime,
    retry,
  };
});
